import { customFieldRepository } from '@webapp/core/data-access/repositories/CustomFieldRepository/CustomFieldRepository'
import { pdfLabelSetRepository } from '@webapp/modules/vendor/data-access/repositories/PDFLabelSetRepository' // eslint-disable-line import/no-internal-modules
import { PDFLabelSections } from '@webapp/modules/vendor/entities/enums/PDFLabelSectionEnum' // eslint-disable-line import/no-internal-modules
import { type PDFLabelFieldMetadataConstructor } from '@webapp/modules/vendor/entities/PDFLabelSet' // eslint-disable-line import/no-internal-modules
import { LABEL_SECTION_MAP } from './constants/labelSectionMap'

/**
 * Custom-field label overrides are stored per CF name in the label set's
 * maps; the kvstore imposes the same 100-char limit as the standard fields.
 */
const CUSTOM_FIELD_LABEL_MAX_LENGTH = 100

const ACTIVE_CUSTOM_FIELD_PARAMS = {
  meta__is_active: 'True',
  page_size: 9999,
}

/**
 * Assemble the PO PDF label editor's field metadata (BHV-033/034/035):
 * the DRF OPTIONS field metadata grouped into the three editor sections via
 * `LABEL_SECTION_MAP`, with active custom-field label rows
 * (`isCustomFieldLabel: true`) appended to PO fields (PO custom fields) and
 * Item fields (order-item custom fields) — never PO costs.
 */
export const getPOPDFLabelEditorFields = async (): Promise<
  PDFLabelFieldMetadataConstructor[]
> => {
  const [fieldMetadata, poCustomFields, orderItemCustomFields] =
    await Promise.all([
      pdfLabelSetRepository.getFieldMetadata(),
      customFieldRepository.getPurchaseOrderCustomFields(
        ACTIVE_CUSTOM_FIELD_PARAMS
      ),
      customFieldRepository.getOrderItemCustomFields(
        ACTIVE_CUSTOM_FIELD_PARAMS
      ),
    ])

  const metadataByFieldKey = new Map(
    fieldMetadata.map((field) => [field.fieldKey, field])
  )

  const sectionFields = (
    section: PDFLabelFieldMetadataConstructor['section']
  ): PDFLabelFieldMetadataConstructor[] =>
    LABEL_SECTION_MAP[section].reduce<PDFLabelFieldMetadataConstructor[]>(
      (acc, fieldKey) => {
        const field = metadataByFieldKey.get(fieldKey)

        if (field) {
          acc.push({ ...field, section, isCustomFieldLabel: false })
        }

        return acc
      },
      []
    )

  const customFieldLabelRows = (
    names: string[],
    section: PDFLabelFieldMetadataConstructor['section']
  ): PDFLabelFieldMetadataConstructor[] =>
    names.map((name) => ({
      fieldKey: name,
      defaultLabel: name,
      section,
      maxLength: CUSTOM_FIELD_LABEL_MAX_LENGTH,
      isCustomFieldLabel: true,
    }))

  return [
    ...sectionFields(PDFLabelSections.PO_FIELDS),
    ...customFieldLabelRows(
      poCustomFields.data.map((cf) => cf.name),
      PDFLabelSections.PO_FIELDS
    ),
    ...sectionFields(PDFLabelSections.PO_COSTS),
    ...sectionFields(PDFLabelSections.ITEM_FIELDS),
    ...customFieldLabelRows(
      orderItemCustomFields.data.map((cf) => cf.name),
      PDFLabelSections.ITEM_FIELDS
    ),
  ]
}
